import React, { useState } from 'react';
import { api, storeSession } from '../api.js';
import { errMessage, makeT } from '../i18n.js';
import LangPicker from '../LangPicker.jsx';
import GoogleButton from '../GoogleButton.jsx';
import Icon from '../Icon.jsx';

/**
 * Giriş ekranı. Üç yol tek kartta: e-posta/şifre (+Google), tablet PIN'i ve
 * yeni restoran kaydı.
 *
 * KARARLAR:
 *  · Sekmeler SEGMENT denetimi: üç ayrı düğme, seçili olanı dolu mavi
 *    olunca ekranda iki "birincil" düğme görünüyordu (sekme + gönder) ve
 *    hangisinin işi yaptığı belirsizdi.
 *  · Her sekme bir `<form>`: Enter'a basmak işe yaramıyordu — şifre
 *    alanından Enter'lamak giriş ekranının en doğal hareketi.
 *  · Google düğmesi yalnız "giriş" sekmesinde: kayıt için restoran adı ve
 *    ülke gerekiyor, Google bunları veremez.
 */
export default function Login({ onDone, lang, onPickLang }) {
  const t = makeT(lang);
  // Landing'deki «Ücretsiz dene» düğmesi doğrudan kayıt sekmesine düşsün
  // (`?tab=register`): kişiyi giriş formuna atıp "kayıt nerede" dedirtmek
  // dönüşümün en ucuz kaybı.
  const [tab, setTab] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search).get('tab');
      return ['login', 'pin', 'register'].includes(q) ? q : 'login';
    } catch { return 'login'; }
  }); // login | pin | register
  const [form, setForm] = useState({ country: 'CH' });
  const [choices, setChoices] = useState(null); // birden fazla restoran
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function go(path, body) {
    setErr('');
    setBusy(true);
    try {
      const data = await api(path, { method: 'POST', body, retry: false });
      storeSession({ accessToken: data.accessToken, refreshToken: data.refreshToken, kind: 'staff' });
      onDone(data);
    } catch (e) {
      // Aynı e-posta birden fazla restoranda → kullanıcıya seçim sun
      if (e.code === 'choose_restaurant') setChoices(e.body.restaurants);
      setErr(errMessage(t, e));
    } finally {
      setBusy(false);
    }
  }

  const submit = (fn) => (e) => { e.preventDefault(); if (!busy) fn(); };

  const TABS = [['login', t('login.tabLogin')], ['pin', 'PIN'], ['register', t('login.tabRegister')]];

  return (
    <div className="center">
      <div className="login">
        <div className="login-brand">
          {/* Panel sabit açık temada (styles.css) → açık zemin varyantı.
              `-light` = açık zemin İÇİN, yani koyu yazılı olan. */}
          <img src="/wordmark-light.png" alt="OrderCEO" />
          <p className="muted">{t('login.subtitle')}</p>
        </div>

        <div className="card login-card">
          <div className="seg" role="tablist">
            {TABS.map(([k, label]) => (
              <button key={k} role="tab" type="button" aria-selected={tab === k}
                onClick={() => { setTab(k); setErr(''); setChoices(null); }}>{label}</button>
            ))}
          </div>

          {tab === 'login' && (
            <form onSubmit={submit(() => go('/api/auth/login', { email: form.email, password: form.password, slug: form.slug || undefined }))}>
              {/* Google ile giriş — personelin e-posta sahipliği Google'da
                  doğrulanmış olur, ayrıca şifre tutmamız gerekmez. */}
              <GoogleButton onCredential={(idToken) => go('/api/auth/google', { idToken, slug: form.slug || undefined })} />
              <p className="sep">{t('or')}</p>
              <div className="field">
                <label htmlFor="lg-email">{t('email')}</label>
                <input id="lg-email" type="email" value={form.email || ''} onChange={set('email')} autoComplete="username" />
              </div>
              <div className="field">
                <label htmlFor="lg-pass">{t('password')}</label>
                <input id="lg-pass" type="password" value={form.password || ''} onChange={set('password')} autoComplete="current-password" />
              </div>
              {choices && (
                <div className="field">
                  <label htmlFor="lg-slug">{t('login.restaurant')}</label>
                  <select id="lg-slug" value={form.slug || ''} onChange={set('slug')}>
                    <option value="">{t('login.choose')}</option>
                    {choices.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <button className="primary block" type="submit" disabled={busy}>
                <Icon n="login" />{t('login.tabLogin')}
              </button>
            </form>
          )}

          {tab === 'pin' && (
            <form onSubmit={submit(() => go('/api/auth/pin', { slug: form.slug, pin: form.pin }))}>
              <div className="field">
                <label htmlFor="pn-slug">{t('login.slug')}</label>
                <input id="pn-slug" value={form.slug || ''} onChange={set('slug')} placeholder="orderceo-test" autoCapitalize="none" />
              </div>
              <div className="field">
                <label htmlFor="pn-pin">PIN</label>
                <input id="pn-pin" value={form.pin || ''} onChange={set('pin')} inputMode="numeric" maxLength={6} autoComplete="off" />
              </div>
              <button className="primary block" type="submit" disabled={busy}>
                <Icon n="keypad" />{t('login.withPin')}
              </button>
            </form>
          )}

          {tab === 'register' && (
            <form onSubmit={submit(() => go('/api/auth/register', { ...form, locale: lang }))}>
              <div className="field">
                <label htmlFor="rg-name">{t('login.restaurantName')}</label>
                <input id="rg-name" value={form.restaurantName || ''} onChange={set('restaurantName')} />
              </div>
              <div className="field">
                <label htmlFor="rg-country">{t('login.country')}</label>
                <select id="rg-country" value={form.country} onChange={set('country')}>
                  <option value="CH">{t('login.countryCH')}</option>
                  <option value="NL">{t('login.countryNL')}</option>
                </select>
                <p className="hint">{t('login.currencyNote')}</p>
              </div>
              <div className="field">
                <label htmlFor="rg-your">{t('login.yourName')}</label>
                <input id="rg-your" value={form.name || ''} onChange={set('name')} autoComplete="name" />
              </div>
              <div className="field">
                <label htmlFor="rg-email">{t('email')}</label>
                <input id="rg-email" type="email" value={form.email || ''} onChange={set('email')} autoComplete="email" />
              </div>
              <div className="field">
                <label htmlFor="rg-pass">{t('login.passwordMin')}</label>
                <input id="rg-pass" type="password" value={form.password || ''} onChange={set('password')} autoComplete="new-password" />
              </div>
              <button className="primary block" type="submit" disabled={busy}>
                <Icon n="userPlus" />{t('login.create')}
              </button>
            </form>
          )}

          {err && <p className="alert" role="alert">{err}</p>}
        </div>

        <div className="login-foot">
          <LangPicker lang={lang} onPick={onPickLang} />
          <a href="/admin">{t('login.superAdmin')}</a>
        </div>
      </div>
    </div>
  );
}
